/**
 * Paletas para pintar matrices sobre la fotografía (RGBA, sin dependencias de DOM).
 * - Secuencial (densidad): perceptualmente ordenada (aprox. «inferno»), transparencia proporcional.
 * - Divergente (diferencia A−B): azul = relativamente más en A, naranja = relativamente más en B,
 *   simétrica alrededor de 0 y transparente por debajo del umbral visible.
 */

type RGB = [number, number, number];

const SEQUENTIAL_STOPS: Array<[number, RGB]> = [
  [0.0, [0, 0, 4]],
  [0.15, [40, 11, 84]],
  [0.3, [101, 21, 110]],
  [0.45, [159, 42, 99]],
  [0.6, [212, 72, 66]],
  [0.75, [245, 125, 21]],
  [0.9, [250, 193, 39]],
  [1.0, [252, 255, 164]],
];

export const DIVERGING_A: RGB = [33, 102, 172];
export const DIVERGING_B: RGB = [224, 130, 20];

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

export function sequentialColor(t: number): RGB {
  const v = Math.min(1, Math.max(0, t));
  for (let i = 1; i < SEQUENTIAL_STOPS.length; i++) {
    const [p1, c1] = SEQUENTIAL_STOPS[i]!;
    const [p0, c0] = SEQUENTIAL_STOPS[i - 1]!;
    if (v <= p1) {
      const k = (v - p0) / (p1 - p0);
      return [Math.round(lerp(c0[0], c1[0], k)), Math.round(lerp(c0[1], c1[1], k)), Math.round(lerp(c0[2], c1[2], k))];
    }
  }
  return SEQUENTIAL_STOPS[SEQUENTIAL_STOPS.length - 1]![1];
}

export interface RenderOptions {
  /** Valor que corresponde al color máximo (usar la misma escala en ambos grupos). */
  max: number;
  /** Opacidad global 0–1. */
  opacity: number;
  /** Fracción de `max` por debajo de la cual la celda es transparente (0–1). */
  threshold?: number;
}

/** RGBA de una matriz de densidad con escala secuencial. */
export function renderSequential(values: ArrayLike<number>, opts: RenderOptions): Uint8ClampedArray {
  const out = new Uint8ClampedArray(values.length * 4);
  const threshold = opts.threshold ?? 0.02;
  for (let i = 0; i < values.length; i++) {
    const t = opts.max > 0 ? values[i]! / opts.max : 0;
    if (t <= threshold) continue;
    const [r, g, b] = sequentialColor(0.15 + 0.85 * t);
    out[i * 4] = r;
    out[i * 4 + 1] = g;
    out[i * 4 + 2] = b;
    out[i * 4 + 3] = Math.round(255 * opts.opacity * Math.min(1, 0.25 + 0.75 * Math.sqrt(t)));
  }
  return out;
}

/** RGBA de una matriz firmada con paleta divergente simétrica. */
export function renderDiverging(values: ArrayLike<number>, opts: RenderOptions): Uint8ClampedArray {
  const out = new Uint8ClampedArray(values.length * 4);
  const threshold = opts.threshold ?? 0.1;
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    const t = opts.max > 0 ? Math.abs(v) / opts.max : 0;
    if (t <= threshold) continue;
    const [r, g, b] = v > 0 ? DIVERGING_A : DIVERGING_B;
    out[i * 4] = r;
    out[i * 4 + 1] = g;
    out[i * 4 + 2] = b;
    out[i * 4 + 3] = Math.round(255 * opts.opacity * Math.min(1, t));
  }
  return out;
}
